import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {List} from 'immutable';

import {
  addToast,
  Anchor,
  Heading,
  IconButton,
  Text,
  useTooltipWithLiveRegion
} from '@albert-io/atomic';

import {getIndiePracticeSubjectsQuery} from '../StudentDashboard.queries';

import IndiePracticeTabs from './IndiePracticeTabs/IndiePracticeTabs.react';

import './indie-practice.scss';

const IndiePracticeViewer = ({className}) => {
  const tooltipAnchorRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState(List());

  const {
    closeTooltip,
    handleEscape,
    openTooltip,
    tooltipContentRef,
    tooltipProps,
    TooltipWithLiveRegion
  } = useTooltipWithLiveRegion(tooltipAnchorRef);

  useEffect(() => {
    async function fetchSubjects() {
      try {
        const res = await getIndiePracticeSubjectsQuery().getResourcePromise();
        setSubjects(res);
        setIsReady(true);
      } catch (err) {
        setIsReady(true);
        setError(
          'We encountered an issue retrieving your available subjects for independent practice. Please try again later.'
        );
      }
    }
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (error) {
      addToast({
        title: 'Oops! Something went wrong',
        type: 'danger',
        message: error
      });
      setError(null);
    }
  }, [error]);

  if (!isReady) {
    return null;
  }

  const curriculumAreas = subjects
    .map((subject) => subject.getCurriculumArea())
    .reduce((prev, curr) => {
      return prev.findIndex((ca) => ca.getId() === curr.getId()) === -1 ? prev.push(curr) : prev;
    }, List())
    .sortBy((ca) => ca.getName().toLowerCase());

  const otherIndex = curriculumAreas.findIndex((ca) => ca.getName() === 'Other');
  const sortedColumnAreas =
    otherIndex !== -1
      ? curriculumAreas.delete(otherIndex).push(curriculumAreas.get(otherIndex))
      : curriculumAreas;

  return (
    <div
      className={classnames('ip__wrapper u-display_flex u-flex-wrap_wrap', className, {
        'ip__wrapper--no-subjects': subjects.isEmpty()
      })}
    >
      <div
        className={classnames(
          'ip__top u-display_flex u-align-items_center u-mar-r_auto u-position_relative',
          {
            'ip__top--no-subjects': subjects.isEmpty()
          }
        )}
      >
        <Heading as='h3' className='ip__heading u-mar-b_0' size='l'>
          Independent practice
        </Heading>
        <IconButton
          className='u-align-self_center'
          icon={['far', 'question-circle']}
          label='More info'
          size='s'
          onBlur={closeTooltip}
          onClick={openTooltip}
          onKeyDown={handleEscape}
          ref={tooltipAnchorRef}
        />
        <TooltipWithLiveRegion {...tooltipProps} placement='right'>
          You can independently practice on questions in these subjects.{' '}
          <Anchor
            as='a'
            href='https://help.albert.io/en/articles/4551366-student-faqs-about-independent-practice'
            inheritColor
            onBlur={closeTooltip}
            ref={tooltipContentRef}
            rel='noopener noreferrer'
            target='_blank'
            underlined
          >
            Learn more
          </Anchor>
        </TooltipWithLiveRegion>
      </div>
      {subjects.isEmpty() ? (
        <Text as='div' className='u-width_100pc u-mar-tb_5' emphasis='lowest' italic>
          No subjects available for practice.
        </Text>
      ) : (
        <IndiePracticeTabs curriculumAreas={sortedColumnAreas} subjects={subjects} />
      )}
    </div>
  );
};

IndiePracticeViewer.propTypes = {
  className: PropTypes.string
};

export default IndiePracticeViewer;
